import { useState, useMemo, useEffect, useCallback, type ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  TrendingUp,
  TrendingDown,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  RotateCcw,
  Zap,
  Crown,
  Sparkles,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatNIS(agorot: number): string {
  return `\u20AA${(agorot / 100).toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BillingStatus = "paid" | "failed" | "refunded";
type FilterStatus = "all" | BillingStatus;

interface BillingLogRow {
  readonly id: string;
  readonly tenant_id: string;
  readonly tenant_name: string;
  readonly tenant_slug: string;
  readonly amount: number;
  readonly status: BillingStatus;
  readonly cardcom_transaction_id: string | null;
  readonly invoice_url: string | null;
  readonly error: string | null;
  readonly charged_at: string;
}

interface BillingLogMeta {
  readonly total: number;
  readonly page: number;
  readonly limit: number;
}

interface PlatformKPIs {
  readonly mrr: number;
  readonly arr: number;
  readonly churnRate: number;
  readonly netNewMrr: number;
  readonly activeCount: number;
  readonly suspendedCount: number;
  readonly totalRevenueThisMonth: number;
}

interface TierData {
  readonly name: string;
  readonly price: number;
  readonly icon: ReactNode;
  readonly gradient: string;
  readonly barColor: string;
  readonly textColor: string;
  readonly borderColor: string;
  readonly recommended?: boolean;
}

// ---------------------------------------------------------------------------
// Static plan tiers — prices are config, not from DB
// ---------------------------------------------------------------------------

const PLAN_TIERS: ReadonlyArray<TierData> = [
  {
    name: "בסיסי",
    price: 14900,
    icon: <Zap className="h-5 w-5" />,
    gradient:
      "from-blue-500/10 to-blue-600/5 dark:from-blue-500/20 dark:to-blue-600/10",
    barColor: "bg-blue-500",
    textColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-blue-200 dark:border-blue-800",
  },
  {
    name: "פרו",
    price: 24900,
    icon: <Sparkles className="h-5 w-5" />,
    gradient:
      "from-violet-500/10 to-violet-600/5 dark:from-violet-500/20 dark:to-violet-600/10",
    barColor: "bg-violet-500",
    textColor: "text-violet-600 dark:text-violet-400",
    borderColor: "border-violet-200 dark:border-violet-800",
    recommended: true,
  },
  {
    name: "ארגוני",
    price: 49900,
    icon: <Crown className="h-5 w-5" />,
    gradient:
      "from-amber-500/10 to-amber-600/5 dark:from-amber-500/20 dark:to-amber-600/10",
    barColor: "bg-amber-500",
    textColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-amber-200 dark:border-amber-800",
  },
] as const;

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

interface StatusConfig {
  readonly label: string;
  readonly badge: "success" | "danger" | "warning";
  readonly icon: ReactNode;
  readonly rowBg: string;
}

const STATUS_CONFIG: Record<BillingStatus, StatusConfig> = {
  paid: {
    label: "שולם",
    badge: "success",
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    rowBg: "",
  },
  failed: {
    label: "נכשל",
    badge: "danger",
    icon: <AlertCircle className="h-3.5 w-3.5" />,
    rowBg: "bg-red-500/5 dark:bg-red-500/10",
  },
  refunded: {
    label: "הוחזר",
    badge: "warning",
    icon: <RotateCcw className="h-3.5 w-3.5" />,
    rowBg: "bg-yellow-500/5 dark:bg-yellow-500/10",
  },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { readonly status: BillingStatus }): ReactNode {
  const c = STATUS_CONFIG[status];
  const isPulsing = status === "failed";
  return (
    <Badge variant={c.badge} className="gap-1.5 font-medium">
      <span className={`relative flex h-1.5 w-1.5 ${isPulsing ? "animate-pulse" : ""}`}>
        <span
          className={`inline-flex h-1.5 w-1.5 rounded-full ${
            status === "paid"
              ? "bg-green-500"
              : status === "failed"
              ? "bg-red-500"
              : "bg-yellow-500"
          }`}
        />
      </span>
      {c.label}
    </Badge>
  );
}

interface KpiCardProps {
  readonly title: string;
  readonly value: string;
  readonly subtitle?: string | undefined;
  readonly icon: ReactNode;
  readonly gradient: string;
  readonly iconColor: string;
  readonly titleColor: string;
  readonly valueColor: string;
  readonly trend?: { readonly label: string; readonly up: boolean };
}

function KpiCard({
  title,
  value,
  subtitle,
  icon,
  gradient,
  iconColor,
  titleColor,
  valueColor,
  trend,
}: KpiCardProps): ReactNode {
  return (
    <Card
      className={`border-0 shadow-md bg-gradient-to-br ${gradient} backdrop-blur-sm transition-transform hover:scale-[1.01] duration-200`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <p className={`text-xs font-semibold uppercase tracking-widest ${titleColor}`}>
              {title}
            </p>
            <p className={`text-3xl font-extrabold tabular-nums leading-none ${valueColor}`}>
              {value}
            </p>
            {subtitle && (
              <p className={`text-xs ${titleColor} opacity-70`}>{subtitle}</p>
            )}
            {trend && (
              <div className={`flex items-center gap-1 text-xs font-medium ${titleColor}`}>
                {trend.up ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {trend.label}
              </div>
            )}
          </div>
          <div className={`rounded-xl p-2.5 ${iconColor} bg-white/20 dark:bg-black/20`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function BillingPage(): ReactNode {
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [logs, setLogs] = useState<ReadonlyArray<BillingLogRow>>([]);
  const [meta, setMeta] = useState<BillingLogMeta>({ total: 0, page: 1, limit: 20 });
  const [kpis, setKpis] = useState<PlatformKPIs | null>(null);
  const [logsLoading, setLogsLoading] = useState(true);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [kpisError, setKpisError] = useState<string | null>(null);
  const [retryingIds, setRetryingIds] = useState<ReadonlySet<string>>(new Set());

  const fetchLogs = useCallback(() => {
    setLogsLoading(true);
    const params = new URLSearchParams();
    if (filter !== "all") params.set("status", filter);
    params.set("page", String(page));
    params.set("limit", "20");

    fetch(`/super/api/billing/log?${params.toString()}`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; data: BillingLogRow[]; meta: BillingLogMeta }>;
      })
      .then((json) => {
        if (!json.success) throw new Error("API error");
        setLogs(json.data);
        setMeta(json.meta);
        setLogsError(null);
      })
      .catch((err: unknown) => {
        setLogsError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setLogsLoading(false));
  }, [filter, page]);

  const fetchKpis = useCallback(() => {
    setKpisLoading(true);
    fetch("/super/api/kpis", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; data: PlatformKPIs }>;
      })
      .then((json) => {
        if (!json.success) throw new Error("API error");
        setKpis(json.data);
        setKpisError(null);
      })
      .catch((err: unknown) => {
        setKpisError(err instanceof Error ? err.message : "Failed to load KPIs");
      })
      .finally(() => setKpisLoading(false));
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchKpis(); }, [fetchKpis]);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [filter]);

  const handleRetry = useCallback((tenantId: string) => {
    setRetryingIds((prev) => new Set([...prev, tenantId]));
    fetch(`/super/api/billing/retry/${tenantId}`, {
      method: "POST",
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; error?: string }>;
      })
      .then(() => {
        fetchLogs();
        fetchKpis();
      })
      .catch((err: unknown) => {
        console.error("Retry failed:", err instanceof Error ? err.message : err);
      })
      .finally(() => {
        setRetryingIds((prev) => {
          const next = new Set(prev);
          next.delete(tenantId);
          return next;
        });
      });
  }, [fetchLogs, fetchKpis]);

  const filteredLogs = useMemo(() => {
    if (!search) return logs;
    const q = search.toLowerCase();
    return logs.filter(
      (l) =>
        l.tenant_name.toLowerCase().includes(q) ||
        l.tenant_slug.toLowerCase().includes(q),
    );
  }, [logs, search]);

  const filterCounts: Record<FilterStatus, number> = useMemo(() => ({
    all: meta.total,
    paid: logs.filter((l) => l.status === "paid").length,
    failed: logs.filter((l) => l.status === "failed").length,
    refunded: logs.filter((l) => l.status === "refunded").length,
  }), [logs, meta.total]);

  const failedCount = kpis ? 0 : logs.filter((l) => l.status === "failed").length;
  const mrr = kpis?.mrr ?? 0;
  const activeCount = kpis?.activeCount ?? 0;
  const totalRevenueThisMonth = kpis?.totalRevenueThisMonth ?? 0;
  const failedDisplayCount = kpis ? logs.filter((l) => l.status === "failed").length : failedCount;

  const kpiTrend = kpis && kpis.netNewMrr !== 0
    ? {
        label: `${kpis.netNewMrr > 0 ? "+" : ""}${formatNIS(kpis.netNewMrr)} מהחודש שעבר`,
        up: kpis.netNewMrr > 0,
      }
    : undefined;

  const now = new Date();
  const monthLabel = now.toLocaleDateString("he-IL", { month: "long", year: "numeric" });

  return (
    <div className="p-4 md:p-6 space-y-8" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">חיוב פלטפורמה</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            סקירת הכנסות, עסקאות ומנויים
          </p>
        </div>
      </div>

      {/* KPI row */}
      {kpisError && (
        <p className="text-sm text-destructive">{kpisError}</p>
      )}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
        <KpiCard
          title="MRR חודשי"
          value={kpisLoading ? "..." : formatNIS(mrr)}
          icon={<TrendingUp className="h-5 w-5" />}
          gradient="from-emerald-50 to-teal-50/60 dark:from-emerald-950/30 dark:to-teal-900/20"
          iconColor="text-emerald-600 dark:text-emerald-400"
          titleColor="text-emerald-700 dark:text-emerald-400"
          valueColor="text-emerald-900 dark:text-emerald-100"
          {...(!kpisLoading && { subtitle: `${String(activeCount)} דיירים פעילים` })}
          {...(kpis !== null && kpiTrend !== undefined && { trend: kpiTrend })}
        />
        <KpiCard
          title="הכנסה החודש"
          value={kpisLoading ? "..." : formatNIS(totalRevenueThisMonth)}
          subtitle={monthLabel}
          icon={<CreditCard className="h-5 w-5" />}
          gradient="from-blue-50 to-indigo-50/60 dark:from-blue-950/30 dark:to-indigo-900/20"
          iconColor="text-blue-600 dark:text-blue-400"
          titleColor="text-blue-700 dark:text-blue-400"
          valueColor="text-blue-900 dark:text-blue-100"
        />
        <KpiCard
          title="חיובים נכשלו"
          value={String(failedDisplayCount)}
          subtitle={failedDisplayCount > 0 ? "דורשים טיפול" : "הכל תקין"}
          icon={<AlertCircle className="h-5 w-5" />}
          gradient={
            failedDisplayCount > 0
              ? "from-red-50 to-rose-50/60 dark:from-red-950/30 dark:to-rose-900/20"
              : "from-muted/40 to-muted/20"
          }
          iconColor={
            failedDisplayCount > 0
              ? "text-red-600 dark:text-red-400"
              : "text-muted-foreground"
          }
          titleColor={
            failedDisplayCount > 0
              ? "text-red-700 dark:text-red-400"
              : "text-muted-foreground"
          }
          valueColor={
            failedDisplayCount > 0
              ? "text-red-900 dark:text-red-100"
              : "text-foreground"
          }
        />
      </div>

      {/* Plan tiers — static config */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          תוכניות מנוי
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {PLAN_TIERS.map((tier) => (
            <Card
              key={tier.name}
              className={`relative border bg-gradient-to-br ${tier.gradient} ${tier.borderColor} shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden ${tier.recommended ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`}
            >
              {tier.recommended && (
                <div className="absolute top-2 left-2">
                  <Badge variant="default" className="text-[10px] px-1.5 py-0">
                    פופולרי
                  </Badge>
                </div>
              )}
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className={tier.textColor}>{tier.icon}</span>
                  <span className={`font-bold text-sm ${tier.textColor}`}>
                    {tier.name}
                  </span>
                </div>
                <div>
                  <div className="text-2xl font-extrabold tabular-nums">
                    {formatNIS(tier.price)}
                    <span className="text-xs font-normal text-muted-foreground">
                      {" "}
                      / חודש
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground shrink-0">
            עסקאות אחרונות
          </h2>
          {/* Filter button group */}
          <div className="flex items-center gap-1 flex-wrap sm:mr-auto">
            {(["all", "paid", "failed", "refunded"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-150 min-h-[36px] cursor-pointer ${
                  filter === s
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {s === "all" ? "הכל" : STATUS_CONFIG[s].label}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                    filter === s
                      ? "bg-white/20 text-inherit"
                      : "bg-muted-foreground/20"
                  }`}
                >
                  {filterCounts[s]}
                </span>
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="relative w-full sm:w-52">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="חפש דייר..."
              className="pe-9 h-9 text-sm rounded-full border-muted"
            />
          </div>
        </div>

        {/* Loading / error */}
        {logsLoading && (
          <div className="py-12 text-center text-muted-foreground text-sm">טוען...</div>
        )}
        {!logsLoading && logsError && (
          <div className="py-12 text-center text-destructive text-sm">{logsError}</div>
        )}

        {/* Transaction list */}
        {!logsLoading && !logsError && (
          <div className="space-y-2">
            {filteredLogs.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">
                אין רשומות
              </div>
            ) : (
              filteredLogs.map((log, idx) => {
                const cfg = STATUS_CONFIG[log.status];
                const isOdd = idx % 2 === 0;
                const isRetrying = retryingIds.has(log.tenant_id);
                return (
                  <div
                    key={log.id}
                    className={`flex flex-wrap items-center gap-3 rounded-xl px-4 py-3 transition-all duration-150 hover:shadow-sm group ${
                      isOdd
                        ? "bg-muted/20 hover:bg-muted/40"
                        : "bg-background hover:bg-muted/20"
                    } ${cfg.rowBg}`}
                  >
                    {/* Avatar-like initial */}
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                      {log.tenant_name.charAt(0)}
                    </div>
                    {/* Name + error */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">
                        {log.tenant_name}
                      </p>
                      {log.error && (
                        <p className="text-xs text-destructive mt-0.5">
                          {log.error}
                        </p>
                      )}
                    </div>
                    {/* Amount */}
                    <span className="tabular-nums font-bold text-sm">
                      {formatNIS(log.amount)}
                    </span>
                    {/* Status badge */}
                    <StatusBadge status={log.status} />
                    {/* Date */}
                    <span className="text-xs text-muted-foreground min-w-[70px] text-end">
                      {formatDate(log.charged_at)}
                    </span>
                    {/* Retry button for failed */}
                    {log.status === "failed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isRetrying}
                        onClick={() => handleRetry(log.tenant_id)}
                        className="min-h-[32px] text-xs gap-1.5"
                      >
                        <RotateCcw className={`h-3.5 w-3.5 ${isRetrying ? "animate-spin" : ""}`} />
                        {isRetrying ? "טוען..." : "נסה שוב"}
                      </Button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Pagination */}
        {!logsLoading && !logsError && meta.total > meta.limit && (
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-muted-foreground">
              {String(meta.total)} רשומות סה&quot;כ
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="min-h-[36px]"
              >
                הקודם
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page * meta.limit >= meta.total}
                onClick={() => setPage((p) => p + 1)}
                className="min-h-[36px]"
              >
                הבא
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
